<template>
  <div class="content">
    <!-- 授权类型说明与引导到企业套餐 -->
    <PersonalAuthAlert />

    <!-- 个人入门版 -->
    <div class="plan-section">
      <h3 class="plan-section__title">{{ $t('ge-ren-ru-men-ban') }}</h3>
      <div class="fees-list__grid">
        <Fees
          v-for="f in sortedPersonalLiteFeesList"
          :key="f.id"
          :fees="f"
          :rightType="IRightsType.PERSONAL_LITE"
          :selected="f.id === selectedFee?.id && currentRightsType === IRightsType.PERSONAL_LITE"
          :discount="personalLiteAnnualSavings"
          @on-select="handleSelectFee(f, IRightsType.PERSONAL_LITE)"
        />
      </div>
    </div>

    <!-- 个人标准版 -->
    <div class="plan-section">
      <h3 class="plan-section__title">{{ $t('ge-ren-ban-biao-zhun-ban') }}</h3>
      <div class="fees-list__grid">
        <Fees
          v-for="f in sortedPersonalFeesList"
          :key="f.id"
          :fees="f"
          :rightType="IRightsType.PERSONAL"
          :selected="f.id === selectedFee?.id && currentRightsType === IRightsType.PERSONAL"
          :discount="personalAnnualSavings"
          @on-select="handleSelectFee(f, IRightsType.PERSONAL)"
        />
      </div>
    </div>

    <div>
      <CreditTip :selected-fee="selectedFee" />
      <!-- 订阅新套餐 连续订阅入门/标准 提示 -->
      <DiscountTip class="mt-[8px]" @add-coin="handleAddCoin" />
    </div>

    <ViewRightsCompare :personal-rights-types="personalRightsTypes" />
  </div>
</template>

<script setup lang="ts" name="PlanB">
import { getBuriedService } from '@rendora/common/services/buried/index';
import { getI18nService } from '@rendora/common/services/i18n';
import { type IFees, useRightsStore } from '@rendora/common/store/rights';
import { BUY_TYPE, IRightsType } from '@rendora/common/types/enums';
import { formatPrice } from '@rendora/common/utils/transform';
import { storeToRefs } from 'pinia';
import { computed, watch } from 'vue';
import { filterFeesBySubscription } from '../../../constants';
import { usePayment } from '../../../store';
import DiscountTip from '../../discount-tip.vue';
import CreditTip from './CreditTip.vue';
import Fees from './Fees.vue';
import PersonalAuthAlert from './PersonalAuthAlert.vue';
import ViewRightsCompare from './ViewRightsCompare.vue';

const i18nService = getI18nService();
const $t = i18nService.t;

// Props
const props = defineProps<{
  selectedFee?: IFees;
  currentRightsType: IRightsType;
}>();

// Emits
const emit = defineEmits<{
  'update:selectedFee': [value: IFees | undefined];
  'update:currentRightsType': [value: IRightsType];
}>();

const paymentStore = usePayment();

// Store 初始化
const rightsStore = useRightsStore();
// 响应式引用
const { rightsDisplay } = storeToRefs(rightsStore);

// 个人版权益类型列表（用于 RightTable）
const personalRightsTypes = [IRightsType.PERSONAL_LITE, IRightsType.PERSONAL];

// 当前权益类型
const currentRightsType = computed(() => {
  // 如果 props.currentRightsType 是个人版类型，使用它；否则默认使用个人标准版
  if (
    props.currentRightsType === IRightsType.PERSONAL ||
    props.currentRightsType === IRightsType.PERSONAL_LITE
  ) {
    return props.currentRightsType;
  }
  return IRightsType.PERSONAL;
});

// 权益信息：个人标准版
const personalPlanRights = computed(() =>
  rightsDisplay.value?.rights.find(r => r.rights_type === IRightsType.PERSONAL),
);

// 权益信息：个人入门版
const personalLitePlanRights = computed(() =>
  rightsDisplay.value?.rights.find(r => r.rights_type === IRightsType.PERSONAL_LITE),
);

// 排序后的费用列表：个人入门版 - 全款年套餐优先级最高，排在最前面
const sortedPersonalLiteFeesList = computed(() => {
  const fees = filterFeesBySubscription(personalLitePlanRights.value?.rights_by_time || []);
  return [...fees].sort((a, b) => {
    // 全款年套餐（buy_type === PAYMENT && days === 365）优先级最高
    const aIsAnnualPayment = a.buy_type === BUY_TYPE.PAYMENT && a.days === 365;
    const bIsAnnualPayment = b.buy_type === BUY_TYPE.PAYMENT && b.days === 365;

    if (aIsAnnualPayment && !bIsAnnualPayment) return -1;
    if (!aIsAnnualPayment && bIsAnnualPayment) return 1;

    // 其他保持原有顺序
    return 0;
  });
});

// 排序后的费用列表：个人标准版 - 全款年套餐优先级最高，排在最前面
const sortedPersonalFeesList = computed(() => {
  const fees = filterFeesBySubscription(personalPlanRights.value?.rights_by_time || []);
  return [...fees].sort((a, b) => {
    // 全款年套餐（buy_type === PAYMENT && days === 365）优先级最高
    const aIsAnnualPayment = a.buy_type === BUY_TYPE.PAYMENT && a.days === 365;
    const bIsAnnualPayment = b.buy_type === BUY_TYPE.PAYMENT && b.days === 365;

    if (aIsAnnualPayment && !bIsAnnualPayment) return -1;
    if (!aIsAnnualPayment && bIsAnnualPayment) return 1;

    // 其他保持原有顺序
    return 0;
  });
});

// 年付相较月付节省金额：个人入门版
const personalLiteAnnualSavings = computed(() => {
  const costByMonth = personalLitePlanRights.value?.rights_by_time.find(
    v => v.days === 30 && v.buy_type === 0,
  );
  const costByYear = personalLitePlanRights.value?.rights_by_time.find(
    v => v.days === 365 && v.buy_type === 0,
  );
  if (costByMonth && costByYear) {
    return formatPrice(costByMonth?.price * 12 - costByYear.price).string;
  }
  return '';
});

// 年付相较月付节省金额：个人标准版
const personalAnnualSavings = computed(() => {
  const costByMonth = personalPlanRights.value?.rights_by_time.find(
    v => v.days === 30 && v.buy_type === 0,
  );
  const costByYear = personalPlanRights.value?.rights_by_time.find(
    v => v.days === 365 && v.buy_type === 0,
  );
  if (costByMonth && costByYear) {
    return formatPrice(costByMonth?.price * 12 - costByYear.price).string;
  }
  return '';
});

// 选择费用卡片
function handleSelectFee(fee: IFees, rightsType: IRightsType) {
  const buriedService = getBuriedService();
  buriedService?.track('youyan_platform_payment_personal_package', {
    rights_type: rightsType,
    buy_type: fee.buy_type,
    days: fee.days,
    price: fee.price,
    time_identity: fee.time_identity,
  });
  emit('update:selectedFee', fee);
  emit('update:currentRightsType', rightsType);
}

const handleAddCoin = () => {
  paymentStore.handleBuyCredit();
};

// 默认选中个人标准版的全款年套餐
watch(
  [personalPlanRights, personalLitePlanRights],
  ([newPersonalPlanRights, newPersonalLitePlanRights]) => {
    if (!props.selectedFee) {
      // 优先选择个人标准版的年套餐
      if (newPersonalPlanRights?.rights_by_time) {
        const annualPaymentFee = newPersonalPlanRights.rights_by_time.find(
          f => f.buy_type === BUY_TYPE.PAYMENT && f.days === 365,
        );
        if (annualPaymentFee) {
          emit('update:selectedFee', annualPaymentFee);
          emit('update:currentRightsType', IRightsType.PERSONAL);
          return;
        }
      }
      // 如果没有个人标准版年套餐，选择个人入门版的年套餐
      if (newPersonalLitePlanRights?.rights_by_time) {
        const annualPaymentFee = newPersonalLitePlanRights.rights_by_time.find(
          f => f.buy_type === BUY_TYPE.PAYMENT && f.days === 365,
        );
        if (annualPaymentFee) {
          emit('update:selectedFee', annualPaymentFee);
          emit('update:currentRightsType', IRightsType.PERSONAL_LITE);
        }
      }
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.content {
  display: flex;
  flex: 1 1 0%;
  flex-direction: column;
  max-width: 672px;
  padding: 20px 16px 0 16px;
}

.plan-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
}

.plan-section__title {
  font-size: var(--x-text-16-size);
  line-height: var(--x-text-16-line-height);
  font-weight: var(--x-weight-medium);
  color: var(--x-text-1);
  text-align: center;
  margin: 0;
}

.fees-list__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
</style>
